"use client";

import { useReducedMotion } from "framer-motion";
import type * as React from "react";
import { useEffect, useRef, useState } from "react";

import { cn } from "../../lib/cn";

/**
 * LineBlobGlyph — il blob a matita di Ando ridotto a **glifo**, pensato per
 * stare accanto a una parola (le CTA "…con Ando") al posto della sparkle
 * generica. Stesso segno del login/hero (`LineBlob`), stessa mano: chi lo
 * vede nei bottoni riconosce Ando, non "una qualche AI".
 *
 * Differenze rispetto a `LineBlob`, tutte dettate dai 14-16px:
 *  - **5 punti** di controllo invece di 7 — a questa scala sette ondulazioni
 *    si annullano otticamente e il blob torna a sembrare un cerchio storto;
 *  - **fermo a riposo**: il path a riposo è una costante calcolata a modulo,
 *    quindi zero JS e zero `requestAnimationFrame` finché nessuno lo tocca
 *    (`LineBlob` invece tiene il loop acceso per tutta la vita del nodo, e in
 *    una lista di bottoni sarebbe uno spreco);
 *  - **`currentColor`**: eredita il colore del testo, così segue l'host
 *    quando passa a `ando-orange-hover` in hover;
 *  - nessuna reazione al puntatore globale: a 15px il "leaning" non si legge.
 *
 * L'animazione parte quando l'host passa `active` (hover / focus del bottone)
 * e non riparte da zero: un fattore `speed` accelera e decelera il tempo
 * interno, così il blob prende movimento e poi si **assesta** dove si trova,
 * come un elastico che smette di vibrare. Il loop si spegne da solo appena
 * `speed` torna a zero.
 *
 * `prefers-reduced-motion` → resta il glifo fermo, nessun loop.
 */

export interface LineBlobGlyphProps {
  /** Lato in px (default 16). */
  readonly size?: number;
  /**
   * L'host comanda: `true` mentre il blob deve muoversi (es. `busy` di una
   * generazione in corso). **Omesso** → il glifo si arrangia da solo e si
   * anima quando il bottone/link che lo contiene va in hover o focus, così
   * usarlo come icona non costringe ogni host a tenersi uno stato.
   */
  readonly active?: boolean;
  /** Spessore del tratto in px reali (non scala col viewBox). */
  readonly strokeWidth?: number;
  readonly className?: string;
}

const N = 5;
const CX = 20;
const CY = 20;
const BASE_R = 13.5;
const WOBBLE = 2.4;

/** Catmull-Rom → bezier chiuso (stessa mano di `LineBlob`, meno punti). */
function smoothClosedPath(pts: ReadonlyArray<readonly [number, number]>): string {
  const n = pts.length;
  const at = (i: number): readonly [number, number] => pts[((i % n) + n) % n] as [number, number];
  const start = at(0);
  let d = `M ${start[0].toFixed(2)} ${start[1].toFixed(2)}`;
  for (let i = 0; i < n; i++) {
    const p0 = at(i - 1);
    const p1 = at(i);
    const p2 = at(i + 1);
    const p3 = at(i + 2);
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${c1x.toFixed(2)} ${c1y.toFixed(2)}, ${c2x.toFixed(2)} ${c2y.toFixed(2)}, ${p2[0].toFixed(2)} ${p2[1].toFixed(2)}`;
  }
  return `${d} Z`;
}

/** Silhouette al tempo ``t`` (ms). A ``t = 0`` è già asimmetrica: mai un cerchio. */
function glyphPath(t: number): string {
  const pts: Array<[number, number]> = [];
  for (let i = 0; i < N; i++) {
    const a = (i / N) * Math.PI * 2 - Math.PI / 2;
    const wob =
      Math.sin(t * 0.0016 + i * 1.9) * WOBBLE + Math.cos(t * 0.0023 + i * 2.7) * (WOBBLE * 0.6);
    const r = BASE_R + wob;
    pts.push([CX + r * Math.cos(a), CY + r * Math.sin(a)]);
  }
  return smoothClosedPath(pts);
}

const REST_D = glyphPath(0);

export const LineBlobGlyph: React.FC<LineBlobGlyphProps> = ({
  size = 16,
  active,
  strokeWidth = 1.2,
  className,
}) => {
  const reduced = useReducedMotion() ?? false;
  const svgRef = useRef<SVGSVGElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  // Tempo interno: avanza solo mentre il blob è "vivo", così riprendere il
  // movimento non fa mai saltare la forma.
  const clock = useRef(0);
  const speed = useRef(0);
  const [hovered, setHovered] = useState(false);

  // Modalità autonoma: nessun `active` dall'host → ci agganciamo al controllo
  // che ci contiene. Listener passivi, rimossi allo smontaggio.
  const auto = active === undefined;
  useEffect(() => {
    if (!auto || reduced) return;
    const host =
      svgRef.current?.closest("button, a, [role='button']") ?? svgRef.current?.parentElement;
    if (!host) return;
    const on = (): void => setHovered(true);
    const off = (): void => setHovered(false);
    host.addEventListener("pointerenter", on);
    host.addEventListener("pointerleave", off);
    host.addEventListener("focus", on);
    host.addEventListener("blur", off);
    return () => {
      host.removeEventListener("pointerenter", on);
      host.removeEventListener("pointerleave", off);
      host.removeEventListener("focus", on);
      host.removeEventListener("blur", off);
    };
  }, [auto, reduced]);

  const live = active ?? hovered;

  useEffect(() => {
    if (reduced) return;
    // A riposo e già assestato: nessun loop da montare.
    if (!live && speed.current < 0.002) return;

    let raf = 0;
    let last = 0;
    const loop = (ts: number): void => {
      const dt = last === 0 ? 16 : Math.min(48, ts - last);
      last = ts;
      speed.current += ((live ? 1 : 0) - speed.current) * 0.1;
      clock.current += dt * speed.current;
      pathRef.current?.setAttribute("d", glyphPath(clock.current));
      // Uscita: si spegne quando il movimento è finito, non di colpo.
      if (!live && speed.current < 0.002) return;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [live, reduced]);

  return (
    <svg
      ref={svgRef}
      aria-hidden
      role="presentation"
      viewBox="0 0 40 40"
      width={size}
      height={size}
      className={cn("shrink-0 overflow-visible", className)}
    >
      <path
        ref={pathRef}
        d={REST_D}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

LineBlobGlyph.displayName = "LineBlobGlyph";
